"use client";

import { useMemo } from "react";
import {
  ArrowTopRightOnSquareIcon,
  CameraIcon,
  ChartBarIcon,
  ChatBubbleLeftRightIcon,
  CurrencyDollarIcon,
  LockClosedIcon,
  PaperAirplaneIcon,
  SparklesIcon,
} from "@heroicons/react/24/outline";

import { CreateRegistry } from "./create-registry";
import { FavoriteButton } from "./favorite";
import { CreateAlert } from "../list/create-alert";
import { Card, CardFooter, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { InformationCircleIcon } from "@heroicons/react/24/outline";
import { RequiredBuyPremiumButton } from "@/app/_components/required/buy-premium-button";
import { ShareButton } from "@/app/_components/share-button";
import { BuyPremiumConsultancy } from "@/app/_components/buy-premium-consultancy";

import { formatBRL } from "@/helper/currency";
import { cn } from "@/lib/utils";
import { formatDate, formatDateAuction, dateStyleCSS } from "@/helper/date";
import { formatPropertyRegistrationUrl } from "@/helper/property";
import { banks, TABS_ESTATE } from "@/constants";

import telegramGroup from "@/static/telegram-group.json";

import type { House, TabsEstate } from "@/types";

interface AuctionDetailsProps {
  data: House;
  hasPaidPlan: boolean;
  openInfo: (value: TabsEstate) => void;
}

export const AuctionDetails = ({
  data,
  hasPaidPlan,
  openInfo,
}: AuctionDetailsProps) => {
  const {
    uf,
    preco_atual = 0,
    criado_em,
    modalidade_de_venda,
    data_de_licitacao1,
    data_de_licitacao2,
    preco1,
    preco2,
    valor_lucro_liquido_com_corretor,
    roi_com_corretor,
    link,
    valor_de_avaliacao,
    desconto,
    banco,
  } = data;

  const matriculaUrl = useMemo(() => {
    return formatPropertyRegistrationUrl({ ...data, uf });
  }, [data, uf]);

  return (
    <div className="lg:col-span-1">
      <div className="flex flex-col gap-6">
        <Card className="overflow-hidden border-2 border-blue-500">
          <div className="flex flex-col gap-1 p-4 text-white bg-gradient-to-r from-blue-600 to-blue-700">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-bold">Análise Inteligente</h3>
              <SparklesIcon className="w-5 h-5" />
            </div>
            {hasPaidPlan ? null : (
              <p className="text-sm text-blue-100">
                Desbloqueie análises avançadas com IA
              </p>
            )}
          </div>
          <div className="flex flex-col gap-4 p-4">
            <div className="flex flex-col gap-3">
              {hasPaidPlan ? (
                <>
                  <CreateRegistry
                    variant="outline"
                    data={data}
                    url={matriculaUrl}
                    className="flex items-center justify-start"
                  />
                  <CreateRegistry
                    action="diligence"
                    variant="outline"
                    data={data}
                    url={matriculaUrl}
                    className="flex items-center justify-start"
                  />
                  <Button
                    variant="outline"
                    className="justify-start"
                    onClick={() => openInfo(TABS_ESTATE.calculadora.value)}
                  >
                    <ChartBarIcon className="w-4 h-4 mr-2 text-muted-foreground" />
                    Simular lucro
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    variant="outline"
                    className="w-full justify-between"
                    disabled
                  >
                    <div className="flex items-center">
                      <LockClosedIcon className="w-4 h-4 mr-2 text-gray-400" />
                      <span className="text-muted-foreground">
                        Analisar matrícula
                      </span>
                    </div>
                    <Badge
                      variant="outline"
                      className="ml-2 text-xs bg-gray-100"
                    >
                      PRO
                    </Badge>
                  </Button>
                  <Button
                    variant="outline"
                    className="w-full justify-between"
                    disabled
                  >
                    <div className="flex items-center">
                      <LockClosedIcon className="w-4 h-4 mr-2 text-gray-400" />
                      <span className="text-muted-foreground">
                        Diligência prévia do imóvel
                      </span>
                    </div>
                    <Badge
                      variant="outline"
                      className="ml-2 text-xs bg-gray-100"
                    >
                      PRO
                    </Badge>
                  </Button>
                  {/* <Button
                    variant="outline"
                    className="w-full justify-between"
                    disabled
                  >
                    <div className="flex items-center">
                      <LockClosedIcon className="w-4 h-4 mr-2 text-gray-400" />
                      <span className="text-muted-foreground">
                        Analisar edital com IA
                      </span>
                    </div>
                    <Badge
                      variant="outline"
                      className="ml-2 text-xs bg-gray-100"
                    >
                      PRO
                    </Badge>
                  </Button> */}
                  <Button
                    variant="outline"
                    className="w-full justify-between"
                    disabled
                  >
                    <div className="flex items-center">
                      <LockClosedIcon className="w-4 h-4 mr-2 text-gray-400" />
                      <span className="text-muted-foreground">
                        Simular lucro
                      </span>
                    </div>
                    <Badge
                      variant="outline"
                      className="ml-2 text-xs bg-gray-100"
                    >
                      PRO
                    </Badge>
                  </Button>
                </>
              )}
            </div>
            {hasPaidPlan ? null : (
              <div className="flex flex-col gap-4 p-4 text-center bg-blue-50 rounded-lg">
                <div className="flex flex-col gap-1">
                  <h4 className="text-sm font-medium text-blue-800">
                    Desbloqueie todos os recursos
                  </h4>
                  <p className="text-xs text-blue-600">
                    Economize tempo e tome decisões mais inteligentes
                  </p>
                </div>
                <RequiredBuyPremiumButton />
              </div>
            )}
          </div>
        </Card>
        <Card className="p-0 flex flex-col items-start justify-between gap-4 pt-4">
          <CardHeader className="flex flex-col gap-4 items-start px-4 py-0 w-full space-y-0">
            <div className="flex flex-col gap-1">
              <p className="text-sm text-muted-foreground">Valor avaliado:</p>
              <p className="text-base font-semibold line-through">
                {valor_de_avaliacao
                  ? formatBRL(valor_de_avaliacao)
                  : "Lance mínimo a definir"}
              </p>
            </div>
            <div className="flex flex-col gap-1">
              <p className="text-sm text-muted-foreground">
                Lance inicial:{" "}
                <span
                  className={cn(
                    "text-sm",
                    valor_lucro_liquido_com_corretor > 0
                      ? "text-green-600 font-medium"
                      : "text-red-600"
                  )}
                >
                  {desconto ? `(${desconto}% de desconto)` : null}
                </span>
              </p>
              <p
                className={cn(
                  preco_atual ? "text-3xl font-semibold" : "text-2xl"
                )}
              >
                {preco_atual
                  ? formatBRL(preco_atual)
                  : "Valor de avaliação a definir"}
              </p>
              <p className="text-sm">
                <span className="text-muted-foreground">Lucro estimado:</span>{" "}
                {preco_atual ? (
                  <span
                    className={cn(
                      "text-sm",
                      valor_lucro_liquido_com_corretor > 0
                        ? "text-green-600"
                        : "text-red-600"
                    )}
                  >
                    <span className="font-semibold">
                      {formatBRL(valor_lucro_liquido_com_corretor)}
                    </span>{" "}
                    <span className="text-xs text-muted-foreground">
                      (ROI: {`${roi_com_corretor}%`})
                    </span>
                  </span>
                ) : (
                  <span>A definir</span>
                )}
              </p>
            </div>
          </CardHeader>
          <div className="px-4 flex flex-row gap-2 items-center">
            <img
              src={banks[banco].logo}
              alt={`Imóvel em leilão - Banco ${banco}`}
              width={53}
              height={53}
            />
            <p className="text-xs">
              <span className="text-muted-foreground">• Modalidade:</span>{" "}
              {modalidade_de_venda}
            </p>
          </div>
          <CardFooter className="px-4 py-4 flex flex-col items-start gap-2 w-full space-y-0 border-t border-slate-200">
            <p className="text-xs">
              <span className="text-muted-foreground">Publicado em:</span>{" "}
              {formatDate(criado_em)}
            </p>
            {data_de_licitacao2 && data_de_licitacao1 ? (
              <div className="flex flex-col gap-2">
                <p className={`text-xs  ${dateStyleCSS(data_de_licitacao1)}`}>
                  <span className="text-muted-foreground">
                    Data do 1° leilão:
                  </span>{" "}
                  {formatDateAuction(data_de_licitacao1)} por{" "}
                  {formatBRL(preco1)}
                </p>
                <p className={`text-xs ${dateStyleCSS(data_de_licitacao2)}`}>
                  <span className="text-muted-foreground">
                    Data do 2° leilão:
                  </span>{" "}
                  {formatDateAuction(data_de_licitacao2)} por{" "}
                  {formatBRL(preco2)}
                </p>
              </div>
            ) : (
              <p className={`text-xs ${dateStyleCSS(data_de_licitacao1)}`}>
                <span className="text-muted-foreground">Data do leilão:</span>{" "}
                {formatDateAuction(data_de_licitacao1)}
              </p>
            )}
          </CardFooter>
        </Card>
        <div className="flex flex-col gap-8">
          <div className="flex flex-col gap-3">
            <BuyPremiumConsultancy>
              <Button className="justify-start">
                <ChatBubbleLeftRightIcon
                  aria-hidden="true"
                  className="w-4 h-4 mr-2"
                />
                Solicitar análise de um especialista
              </Button>
            </BuyPremiumConsultancy>
            <CreateRegistry
              data={data}
              url={matriculaUrl}
              className="flex items-center justify-start"
            />
            <CreateRegistry
              action="diligence"
              variant="outline"
              data={data}
              url={matriculaUrl}
              className="flex items-center justify-start"
            />
            <Button
              variant="outline"
              className="justify-start"
              onClick={() => openInfo(TABS_ESTATE.calculadora.value)}
            >
              <ChartBarIcon className="w-4 h-4 mr-2 text-muted-foreground" />
              Simular lucro
            </Button>
            <Button variant="outline" className="justify-start" asChild>
              <a href={link} target="_blank" rel="nofollow">
                <ArrowTopRightOnSquareIcon
                  aria-hidden="true"
                  className="w-4 h-4 mr-2 text-muted-foreground"
                />
                Ir para o site do leiloeiro
              </a>
            </Button>
            <ShareButton title="Compartilhar imóvel" />
            <Button variant="outline" className="justify-start" asChild>
              <a
                href={telegramGroup[uf].telegram.link}
                target="_blank"
                rel="nofollow"
              >
                <PaperAirplaneIcon
                  aria-hidden="true"
                  className="w-4 h-4 mr-2 text-muted-foreground"
                />
                Entrar no grupo do Telegram
              </a>
            </Button>
          </div>
          <div className="flex flex-col gap-3">
            <Button
              variant="outline"
              className="justify-start"
              onClick={() => openInfo(TABS_ESTATE.fotos.value)}
            >
              <CameraIcon
                aria-hidden="true"
                className="w-4 h-4 mr-2 text-muted-foreground"
              />
              Fotos
            </Button>
            <FavoriteButton
              fullWidth
              id={data.id}
              favorito={data.favorito}
              variant="outline"
              size="default"
              addLabel="Adicionar aos favoritos"
              removeLabel="Remover dos favoritos"
              className="w-full justify-start"
            />
          </div>
          <div className="py-4 border-t flex flex-col gap-4">
            <div className="flex flex-row items-start">
              <div>
                <InformationCircleIcon
                  aria-hidden="true"
                  className="text-muted-foreground inline w-4 h-4 mr-2"
                />
              </div>
              <p className="text-xs text-muted-foreground">
                Quer ser o primeiro a saber por e-mail de oportunidades
                semelhantes a essa?{" "}
                <CreateAlert uf={uf}>
                  <Button
                    size="sm"
                    variant="link"
                    className="p-0 min-h-6 underline"
                  >
                    Crie um alerta
                  </Button>
                </CreateAlert>
                .
              </p>
            </div>
            <a
              href="#documentos"
              className="text-xs text-muted-foreground hover:underline flex flex-row items-center"
            >
              <div>
                <InformationCircleIcon
                  aria-hidden="true"
                  className="inline w-4 h-4 mr-2"
                />
              </div>
              Consulte o edital e documentos do leilão.
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};
